import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Search,
  Trash2,
} from "lucide-react";
import { Input } from "../components/ui/input";
import {
  fetchAdminReports,
  deleteAdminReport,
  downloadAdminReport,
  clearReportError,
} from "../redux/slices/reportSlice";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { useToast } from "../components/ui/use-toast";
import { formatDateTime } from "../lib/utils";

function statusVariant(status) {
  if (status === "generated") return "default";
  if (status === "failed") return "destructive";
  return "secondary";
}

function Reports() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { reports, pagination, loading, error, downloadId } = useSelector(
    (state) => state.reports
  );

  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const limit = 20;

  useEffect(() => {
    dispatch(
      fetchAdminReports({
        page,
        limit,
        ...(filterType ? { type: filterType } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      })
    );
  }, [dispatch, page, filterType, filterStatus, search]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Reports",
        description: error,
        variant: "destructive",
      });
      dispatch(clearReportError());
    }
  }, [error, toast, dispatch]);

  const handleDownload = async (report) => {
    if (report.status !== "generated") {
      toast({
        title: "Not ready",
        description: "PDF is only available when status is generated.",
        variant: "destructive",
      });
      return;
    }
    try {
      await dispatch(
        downloadAdminReport({ id: report.id, title: report.title })
      ).unwrap();
      toast({ title: "Download started" });
    } catch (err) {
      toast({
        title: "Download failed",
        description: typeof err === "string" ? err : "Could not download",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this report and its file?")) return;
    try {
      await dispatch(deleteAdminReport(id)).unwrap();
      toast({ title: "Report deleted" });
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Delete failed",
        variant: "destructive",
      });
    }
  };

  const totalPages = Math.max(1, pagination.totalPages || 1);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Reports</h1>
        <p className="text-muted-foreground">
          Certificates and PDF reports generated by users across the platform.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>All reports</CardTitle>
            <CardDescription>
              {pagination.total} record(s) — search by title, description, or email
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex gap-2">
              <Input
                placeholder="Search certificates…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setPage(1);
                    setSearch(searchInput);
                  }
                }}
                className="w-[220px]"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setPage(1);
                  setSearch(searchInput);
                }}
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <Select
              value={filterType || "all"}
              onValueChange={(v) => {
                setPage(1);
                setFilterType(v === "all" ? "" : v);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="certificate">certificate</SelectItem>
                <SelectItem value="activity_report">activity_report</SelectItem>
                <SelectItem value="progress_report">progress_report</SelectItem>
                <SelectItem value="custom">custom</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filterStatus || "all"}
              onValueChange={(v) => {
                setPage(1);
                setFilterStatus(v === "all" ? "" : v);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">pending</SelectItem>
                <SelectItem value="generated">generated</SelectItem>
                <SelectItem value="failed">failed</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                dispatch(
                  fetchAdminReports({
                    page,
                    limit,
                    ...(filterType ? { type: filterType } : {}),
                    ...(filterStatus ? { status: filterStatus } : {}),
                    ...(search.trim() ? { search: search.trim() } : {}),
                  })
                )
              }
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && reports.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {r.created_at
                          ? formatDateTime(r.created_at)
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {r.user?.email ? (
                          <Link
                            to={`/users/${r.user_id}`}
                            className="text-sm text-primary hover:underline truncate block max-w-[180px]"
                          >
                            {r.user.email}
                          </Link>
                        ) : (
                          <span className="text-xs font-mono truncate block max-w-[120px]">
                            {r.user_id}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{r.type}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <div className="font-medium truncate">{r.title}</div>
                        {r.description ? (
                          <div className="text-xs text-muted-foreground truncate">
                            {r.description}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(r.status)}>
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={
                            r.status !== "generated" || downloadId === r.id
                          }
                          onClick={() => handleDownload(r)}
                          title="Download PDF"
                        >
                          {downloadId === r.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => handleDelete(r.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!loading && reports.length === 0 && (
                <p className="text-center text-muted-foreground py-10 text-sm">
                  No reports match your filters.
                </p>
              )}

              <div className="flex items-center justify-between pt-6">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      page >= totalPages || loading || reports.length === 0
                    }
                    onClick={() =>
                      setPage((p) => (p >= totalPages ? p : p + 1))
                    }
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Reports;
